import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import { z } from "zod";

import { getStoredCookieConsent } from "../utils/cookie-consent";
import { setCookie } from "../utils/cookies";

export const ZConsentState = z.object({
  functional: z.boolean(),
  analytics: z.boolean(),
  chosen: z.boolean(),
});

export type TConsentState = z.infer<typeof ZConsentState>;

export type TCookieConsentContext = {
  consent: TConsentState;
  acceptAll: () => void;
  rejectAnalytics: () => void;
};

const defaultConsent: TConsentState = {
  functional: true,
  analytics: false,
  chosen: false,
};

export const CookieConsentContext = createContext<TCookieConsentContext>({
  consent: defaultConsent,
  acceptAll: () => {},
  rejectAnalytics: () => {},
});

export const COOKIE_CONSENT = "cookie-consent";

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider = ({ children }: PropsWithChildren) => {
  const [consent, setConsent] = useState<TConsentState>(() => {
    if (typeof window === "undefined") {
      return defaultConsent;
    }

    return getStoredCookieConsent();
  });

  useEffect(() => {
    if (!consent.chosen) {
      return;
    }

    window.gtag?.("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.analytics ? "granted" : "denied",
    });

    if (consent.analytics) {
      window.gtag?.("event", "page_view", {
        page_path:
          window.location.pathname +
          window.location.search +
          window.location.hash,
      });
    }
  }, [consent.analytics, consent.chosen]);

  const acceptAll = () => {
    const newConsent: TConsentState = {
      functional: true,
      analytics: true,
      chosen: true,
    };

    setCookie(COOKIE_CONSENT, JSON.stringify(newConsent));
    setConsent(newConsent);
  };

  const rejectAnalytics = () => {
    const newConsent: TConsentState = {
      functional: true,
      analytics: false,
      chosen: true,
    };

    setCookie(COOKIE_CONSENT, JSON.stringify(newConsent));
    setConsent(newConsent);
  };

  return (
    <CookieConsentContext.Provider
      value={{ consent, acceptAll, rejectAnalytics }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
